import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { storage } from "@/lib/storage";
import { openRazorpayCheckout, razorpayApi } from "@/lib/razorpay";
import { toast } from "sonner";
import { Check, Crown, Sparkles, Zap } from "lucide-react";

const PLAN_ICONS = {
  starter: Sparkles,
  pro: Zap,
  business: Crown,
};

export default function Plans() {
  const navigate = useNavigate();
  const profile = storage.getProfile() || { id: "M000000", name: "Merchant" };
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState({ active: false, subscription: null });
  const [busyPlan, setBusyPlan] = useState(null);

  useEffect(() => {
    razorpayApi.plans().then(({ data }) => setPlans(data.plans));
    razorpayApi.subscription(profile.id).then(({ data }) => setSub(data));
    // eslint-disable-next-line
  }, []);

  const buy = async (plan) => {
    setBusyPlan(plan.id);
    try {
      await openRazorpayCheckout({
        amount: plan.price,
        currency: "INR",
        purpose: "subscription",
        merchantId: profile.id,
        merchantName: profile.name,
        note: `Subscription · ${plan.name}`,
        planId: plan.id,
      });
      toast.success(`${plan.name} plan activated!`);
      const { data } = await razorpayApi.subscription(profile.id);
      setSub(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Purchase failed");
    } finally {
      setBusyPlan(null);
    }
  };

  return (
    <div className="page-shell" data-testid="plans-page">
      <Header title="Choose a Plan" subtitle="Unlock premium merchant features" />

      {sub.active && (
        <div className="card-glass gold-glow rounded-2xl p-5 mb-6 fade-slide-up" data-testid="active-subscription">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[rgba(230,180,85,0.15)] flex items-center justify-center">
              <Check size={20} className="text-[#E6B455]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#F7E6B4]/55">Active plan</div>
              <div className="font-bold text-[#F7E6B4] capitalize text-lg">
                {sub.subscription?.planId || "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4" data-testid="plans-list">
        {plans.map((p) => {
          const Icon = PLAN_ICONS[p.id] || Sparkles;
          const isCurrent = sub.subscription?.planId === p.id;
          const isPro = p.id === "pro";
          return (
            <div
              key={p.id}
              className={`card-glass rounded-2xl p-6 relative overflow-hidden fade-slide-up ${
                isPro ? "gold-glow border-[rgba(230,180,85,0.4)]" : ""
              }`}
              data-testid={`plan-${p.id}`}
            >
              {isPro && (
                <div className="absolute top-3 right-3 bg-gradient-to-br from-[#E6B455] to-[#B8902F] text-[#0A0F1E] text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded">
                  Most popular
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-[rgba(230,180,85,0.1)] flex items-center justify-center">
                  <Icon size={24} className="text-[#E6B455]" />
                </div>
                <div>
                  <div className="text-xl font-bold text-[#F7E6B4]">{p.name}</div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold gold-text">₹{p.price}</span>
                    <span className="text-xs text-[#F7E6B4]/55">/ month</span>
                  </div>
                </div>
              </div>

              <ul className="space-y-2 mb-5">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#F7E6B4]/80">
                    <Check size={14} className="text-[#E6B455] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => buy(p)}
                disabled={isCurrent || busyPlan === p.id}
                className={`w-full py-3 rounded-xl font-semibold ${
                  isPro ? "btn-gold" : "btn-outline-gold"
                } disabled:opacity-50`}
                data-testid={`buy-plan-${p.id}`}
              >
                {isCurrent
                  ? "Current plan"
                  : busyPlan === p.id
                  ? "Opening Razorpay…"
                  : `Choose ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-center text-[11px] text-[#F7E6B4]/40 mt-8">
        Payments by Razorpay · Cancel anytime
      </p>
    </div>
  );
}
